/**
 * Offline Overlay Component
 *
 * Displays a customer-friendly message when connectivity is lost.
 * - Full-screen semi-transparent overlay
 * - Professional airport-style messaging
 * - Auto-hides when connectivity returns
 * - Does NOT clear existing flight data (shows last known state behind overlay)
 */

import React from 'react';
import { useConnectivity } from '../context/ConnectivityContext.jsx';

const OfflineOverlay = () => {
  const { isOnline, isChecking } = useConnectivity();

  // Don't render if online
  if (isOnline) {
    return null;
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.content}>
        {/* WiFi/Connection Icon */}
        <div style={styles.iconContainer}>
          <svg
            style={styles.icon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {/* WiFi icon with X */}
            <path d="M1 9a10 10 0 0 1 17 0" />
            <path d="M5 13a6 6 0 0 1 9 0" />
            <path d="M9 17a2 2 0 0 1 3 0" />
            <line x1="2" y1="2" x2="22" y2="22" style={{ stroke: '#FFD200' }} />
          </svg>
        </div>

        {/* Title */}
        <h1 style={styles.title}>Connection Interrupted</h1>

        {/* Message */}
        <p style={styles.message}>
          We're reconnecting to the network.
          <br />
          Flight information will resume automatically.
        </p>

        {/* Status indicator */}
        <div style={styles.statusContainer}>
          {isChecking ? (
            <div style={styles.spinner} />
          ) : (
            <div style={styles.pulse} />
          )}
          <span style={styles.statusText}>
            {isChecking ? 'Checking connection...' : 'Waiting for network...'}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999,
    backdropFilter: 'blur(4px)',
  },
  content: {
    textAlign: 'center',
    color: '#FFFFFF',
    padding: '40px',
    maxWidth: '600px',
  },
  iconContainer: {
    marginBottom: '30px',
  },
  icon: {
    width: '100px',
    height: '100px',
    color: '#FFFFFF',
    opacity: 0.9,
  },
  title: {
    fontSize: '42px',
    fontWeight: '700',
    marginBottom: '20px',
    color: '#FFFFFF',
    letterSpacing: '1px',
  },
  message: {
    fontSize: '24px',
    lineHeight: '1.5',
    color: '#CCCCCC',
    marginBottom: '40px',
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
  },
  statusText: {
    fontSize: '18px',
    color: '#FFD200',
    fontWeight: '500',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '3px solid rgba(255, 210, 0, 0.3)',
    borderTopColor: '#FFD200',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  pulse: {
    width: '12px',
    height: '12px',
    backgroundColor: '#FFD200',
    borderRadius: '50%',
    animation: 'pulse 2s ease-in-out infinite',
  },
};

// Add keyframe animations via style tag (injected once)
if (typeof document !== 'undefined' && !document.getElementById('offline-overlay-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'offline-overlay-styles';
  styleSheet.textContent = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(1.2); }
    }
  `;
  document.head.appendChild(styleSheet);
}

export default OfflineOverlay;
